import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { auditLogApi, AuditEntry } from '@/lib/auditLog';
import { companiesApi } from '@/lib/companies';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { InlineAlert } from '@/components/app/Modal';
import { JpDate } from '@/components/jp/JpDate';

export function AuditLogPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const companyId = companies[0]?.id;

  const [filter, setFilter] = useState('');

  const { data: entries = [], isLoading, error } = useQuery<AuditEntry[]>({
    queryKey: ['audit-log', companyId],
    queryFn: () => auditLogApi.list(companyId!, 200),
    enabled: !!companyId,
    retry: false,
  });

  const filtered = entries.filter((e) =>
    filter === '' || e.action.includes(filter) || e.target_kind?.includes(filter) || false,
  );

  return (
    <AppShell
      title={t('audit_log.title', { defaultValue: 'Audit log' })}
      description={t('audit_log.subtitle', { defaultValue: 'Every privileged action, time-stamped and immutable.' }) as string}
      actions={
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('audit_log.filter_placeholder', { defaultValue: 'Filter by action or kind' }) as string}
          className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
      }
    >
      <AdminSiblingNav wsId={wsId!} current="audit-log" />
      <div className="space-y-4">
        {error && (
          <InlineAlert tone="error">{(error as Error).message}</InlineAlert>
        )}

        {isLoading && (
          <div className="card-paper p-10 text-center text-ink-500 text-sm">
            {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="card-paper p-12 text-center">
            <div className="text-[13px] text-ink-500">{t('audit_log.empty', { defaultValue: 'No audit events match this filter yet.' })}</div>
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className="card-paper overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100">
                  <th className="py-2.5 pl-5 pr-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('audit_log.th_when')}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('audit_log.th_actor')}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('audit_log.th_action')}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('audit_log.th_target')}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('audit_log.th_payload')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-b border-ink-50 align-top hover:bg-paper-100/40">
                    <td className="py-2 pl-5 pr-3 num text-ink-700 text-xs whitespace-nowrap">
                      <JpDate value={e.created_at} />
                    </td>
                    <td className="py-2 px-3 num text-xs text-ink-500 truncate max-w-[14ch]">
                      {e.user_id?.slice(0, 8) ?? '—'}
                    </td>
                    <td className="py-2 px-3">
                      <span className="chip-citation">{e.action}</span>
                    </td>
                    <td className="py-2 px-3 num text-ink-700 text-xs">
                      {e.target_kind ?? ''}
                      <span className="text-ink-400">
                        {e.target_id ? ` · ${String(e.target_id).slice(0, 12)}` : ''}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-ink-600 max-w-[40ch] truncate">
                      {e.payload ? JSON.stringify(e.payload) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
